import CountUp from "react-countup";
import {useTranslation} from "react-i18next";
import React, {useCallback, useEffect, useState} from "react";
import {Skeleton, Tooltip} from "antd";
import {APIv1} from "../../../../api";
import {deviceStatusInactiveIcons} from "../../../../utils/deviceUtils";
import {images} from "../../../../constants";
import '../BaseNumberCardStyle.css'

interface TransactionCountCardProps {
    successAmount?: number;
    failureAmount?: number;
}

const DeviceLastStatusCard: React.FC<TransactionCountCardProps> = () => {
    const {t} = useTranslation();
    const [userData, setUserData] = useState({});
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchDeviceData = useCallback(async () => {
        setLoading(true);

        try {
            const {data} = await APIv1.get('/device/recent/status/', {
                headers: {
                    Authorization: `Token ${userData.token}`,
                },
            });

            const {
                total,
                online,
                offline_minute,
                offline_hour,
                offline_day,
            } = data;

            const percent = (value) =>
                total > 0 ? ((value / total) * 100).toFixed(2) : 0;

            setData({
                totalCount: total,
                activeCount: online,
                inactiveMinuteCount: offline_minute,
                inactiveHourCount: offline_hour,
                inactiveDayCount: offline_day,

                activeCountPercent: percent(online),
                inactiveMinuteCountPercent: percent(offline_minute),
                inactiveHourCountPercent: percent(offline_hour),
                inactiveDayCountPercent: percent(offline_day),
            });
        } catch (error) {
            console.error('Error fetching device data:', error);
        } finally {
            setLoading(false);
        }
    }, [userData.token]);

    useEffect(() => {
        if (!userData.token) return;

        fetchDeviceData()
    }, [userData.token])


    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'));
        if (items) {
            setUserData(items);
        }
    }, [userData.token]);

    const rows = [
        {
            key: 'online',
            tone: 'online',
            label: t('common.online'),
            tick: t('common.online'),
            hover: t('common.online'),
            icon: images.deviceActive,
            count: data.activeCount,
            percent: data.activeCountPercent,
        },
        {
            key: 'minute',
            tone: 'minute',
            label: t("analysis.numbersStats.labels.inactiveMinute"),
            tick: t("analysis.numbersStats.ticks.minute"),
            hover: t("analysis.numbersStats.hoverLabels.inactiveMinute"),
            icon: deviceStatusInactiveIcons['minute'],
            count: data.inactiveMinuteCount,
            percent: data.inactiveMinuteCountPercent,
        },
        {
            key: 'hour',
            tone: 'hour',
            label: t("analysis.numbersStats.labels.inactiveHour"),
            tick: t("analysis.numbersStats.ticks.hour"),
            hover: t("analysis.numbersStats.hoverLabels.inactiveHour"),
            icon: deviceStatusInactiveIcons['hour'],
            count: data.inactiveHourCount,
            percent: data.inactiveHourCountPercent,
        },
        {
            key: 'day',
            tone: 'day',
            label: t("analysis.numbersStats.labels.inactiveDay"),
            tick: t("analysis.numbersStats.ticks.day"),
            hover: t("analysis.numbersStats.hoverLabels.inactiveDay"),
            icon: deviceStatusInactiveIcons['day'],
            count: data.inactiveDayCount,
            percent: data.inactiveDayCountPercent,
        },
    ];

    return (
        <div className="analysis__metrics__card-device__last_status analysis__metrics__card">
            <div className="analysis__metrics__status-header">
                <h2>{t("analysis.numbersStats.mainTitles.devicesLastStatusTitle")}</h2>
                {loading ? (
                    <Skeleton.Button active style={{width: 60, height: 16}}/>
                ) : (
                    <Tooltip title={t('common.total')}>
                        <span className="analysis__metrics__card-header-total">
                            <img
                                className="analysis__metrics__icon"
                                src={deviceStatusInactiveIcons['total']}
                                alt="total"
                                width={14}
                                height={14}
                            />
                            <CountUp end={data.totalCount} duration={3}/>
                        </span>
                    </Tooltip>
                )}
            </div>
            <div className="analysis__device-hottest-scale analysis__metrics__status-scale">
                {rows.map(({key, tone, tick, count}) => (
                    <div
                        className="analysis__device-hottest-scale-step"
                        key={key}
                        style={{flexGrow: loading ? 1 : Number(count) || 0}}
                    >
                        <span
                            className={`analysis__device-hottest-segment analysis__metrics__share-fill--${tone}${!loading && !Number(count) ? " analysis__device-hottest-segment--empty" : ""}`}
                        >
                            {!loading && count}
                        </span>
                        <span className="analysis__device-hottest-tick">{tick}</span>
                    </div>
                ))}
            </div>
            <div className="analysis__metrics__container">
                {rows.map(({key, tone, label, hover, icon, count, percent}) => (
                    <div className="analysis__metrics__card" key={key}>
                        <span className="analysis__metrics__label">{label}</span>
                        <div className="metrics-value">
                            <div className="metrics-value__row">
                                <Tooltip title={hover}>
                                    <img
                                        className="analysis__metrics__icon"
                                        src={icon}
                                        alt={key}
                                        style={{width: 18, height: 18}}
                                    />
                                </Tooltip>
                                <span className="metrics-value__count">
                                    {loading ? (
                                        <Skeleton.Button active style={{width: 60, height: 24}}/>
                                    ) : (
                                        <CountUp end={count} duration={3}/>
                                    )}
                                </span>
                            </div>

                            {!loading && (
                                <span className="metrics-value__percent">
                                    {percent}%
                                </span>
                            )}
                        </div>
                        <div className="analysis__metrics__share">
                            <span
                                className={`analysis__metrics__share-fill analysis__metrics__share-fill--${tone}`}
                                style={{width: loading ? 0 : `${percent}%`}}
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default DeviceLastStatusCard;
